// ProjectDesignBar.jsx
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import AWS from "aws-sdk";
import {
  createEntityManager,
  resolveVarsFromContext,
} from "../api/EntityManager";
import PlanModule from "../components/PlanModule";

import { buildEntityFromOntology } from "../api/OntologyUtils";

import { loadOntology } from "../api/OntologyUtils";

import "./ProjectDesignBar.css";

export default function ProjectDesignBar({
  userId,

  projects = [],
  selectedProjectId,
  onProjectSelect,
  roomOptions,
  setRoomOptions,
  selectedRoom,
  handleRoomSelectionChange,
}) {
  const roomDetails = roomOptions.find((room) => room.id === selectedRoom);

  const outlet = useOutletContext?.() || {};

  const ctxForm = outlet.ctxForm || {}; // may include projectUuid, globalId, s3Prefix, etc.

  // Dropdown + camera state
  const [cameraDropdown, setCameraDropdown] = useState({ open: null });
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraModal, setCameraModal] = useState({
    open: false,
    globalId: null,
  });

  // Hidden file input for uploads
  const fileInputRef = useRef(null);
  const uploadglobalIdRef = useRef(null);

  // Save status
  const [savingImage, setSavingImage] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // New Room UI state
  const [newRoomUI, setNewRoomUI] = useState({
    open: false,
    title: "",
    saving: false,
    error: null,
  });

  // Slugify helper
  function slugify(str) {
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
  }

  // Make slug unique against existing roomOptions
  function ensureUniqueSlug(base) {
    const existing = new Set(roomOptions.map((r) => r.id));
    let slug = base;
    let i = 2;
    // Room globalId shape: room:{PROJECT_UUID}:{ROOM_ID}
    const mkId = (s) =>
      `room:${ctxForm.projectUuid || selectedProjectId || "unknown"}:${s}`;
    while (existing.has(mkId(slug))) {
      slug = `${base}-${i++}`;
    }
    return slug;
  }

  function normalizeProjectToken(input) {
    if (!input) return null;
    let v = String(input).trim();
    // Normalize to "project_<uuid>"
    if (/^project:/.test(v)) v = v.replace(/^project:/, "project_");
    if (/^[0-9a-fA-F-]{36}$/.test(v)) v = `project_${v}`;
    return v;
  }

  // Use underscores to match your example (front_room)
  function slugRoom(name) {
    return String(name || "room")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  function ensureUniqueRoomSlug(baseSlug, projectToken, existingGlobalIds) {
    const mkId = (slug) => `room:${projectToken}:${slug}`;
    if (!existingGlobalIds.has(mkId(baseSlug))) return baseSlug;
    let i = 2;
    while (existingGlobalIds.has(mkId(`${baseSlug}_${i}`))) i += 1;
    return `${baseSlug}_${i}`;
  }

  async function createNewRoom() {
    const selectedProjectToken = normalizeProjectToken(
      ctxForm.projectUuid || selectedProjectId,
    );

    if (!selectedProjectToken) {
      setNewRoomUI((u) => ({ ...u, error: "Select a project first." }));
      return;
    }

    const title = (newRoomUI.title || "").trim() || "New Room";
    const baseSlug = slugRoom(title);

    // ensure uniqueness inside this project
    const existingIds = new Set(roomOptions.map((r) => r.id));
    const roomSlug = ensureUniqueRoomSlug(
      baseSlug,
      selectedProjectToken,
      existingIds,
    );

    // Compose the canonical room id (matches your example)
    const roomGlobalId = `room:${selectedProjectToken}:${roomSlug}`;

    setNewRoomUI((u) => ({ ...u, saving: true, error: null }));
    try {
      // 1) Load ontology & manager
      const ontologyObj = await loadOntology("room");
      const mgr = createEntityManager(ontologyObj);

      // 2) Ensure AWS creds
      await AWS.config.credentials.getPromise();
      const awsCredsResolved = AWS.config.credentials;

      // 3) Vars for templates (exactly as you specified)
      const vars = {
        COGNITO_ID: awsCredsResolved?.identityId,
        PROJECT_UUID: selectedProjectToken,
        ROOM_ID: roomSlug,
      };

      // 4) Build default skeleton from ontology
      const entity = buildEntityFromOntology(ontologyObj, {
        awsCreds: awsCredsResolved,
        vars,
      });

      // 5) Fill required defaults from the name/example
      const now = new Date().toISOString();
      entity.id = entity.id || roomGlobalId; // "room:project_<uuid>:front_room"
      entity.projectId = selectedProjectToken; // "project_<uuid>"
      entity.title = title; // "Front Room"
      entity.kind = roomSlug; // "front_room"
      entity.status = entity.status || "unscoped"; // default status
      entity.createdAt = entity.createdAt || now; // ISO
      entity.updatedAt = now;

      // 6) Persist (upsert)
      await mgr.update({ vars, patch: entity, aws: awsCredsResolved });

      // 7) Reflect in UI and select it
      setRoomOptions((prev) => [
        ...prev,
        {
          id: entity.id,
          title: entity.title,
          description: entity.description || "",
          image: entity.image || null,
        },
      ]);
      handleRoomSelectionChange?.({ target: { value: entity.id } });

      // 8) Reset UI
      setNewRoomUI({ open: false, title: "", saving: false, error: null });
    } catch (err) {
      console.error("[Room Create][error]", err);
      setNewRoomUI((u) => ({
        ...u,
        saving: false,
        error: err?.message || String(err),
      }));
    }
  }

  // If there is only one project, select it by default
  useEffect(() => {
    if (!selectedProjectId && projects?.length === 1 && onProjectSelect) {
      onProjectSelect(projects[0]?.id);
    }
  }, [projects, selectedProjectId, onProjectSelect]);

  function updateRoomImage(globalId, imageDataUrl) {
    setRoomOptions((prev) =>
      prev.map((room) =>
        room.id === globalId ? { ...room, image: imageDataUrl } : room,
      ),
    );
  }

  async function saveRoomImage(globalId, imageDataUrl) {
    setSavingImage(true);
    setSaveError(null);

    try {
      if (!imageDataUrl) throw new Error("No image provided.");

      // 1) Load ontology & build manager
      const roomOntology = await loadOntology("room");
      const mgr = createEntityManager(roomOntology);

      // 2) Pick the room id to operate on
      const roomIdStr =
        globalId ||
        ctxForm?.roomGlobalId ||
        roomOptions.find((r) => r.id === selectedRoom)?.id;

      if (!roomIdStr) {
        throw new Error("Room id not found in context.");
      }

      // 3) Resolve vars for key construction (let EM parse {PROJECT_UUID, ROOM_ID} from id_pattern)
      const vars = resolveVarsFromContext(mgr.meta, {
        userId, // {COGNITO_ID}
        id: roomIdStr, // e.g. "room:project_...:bedroom" → {PROJECT_UUID, ROOM_ID}
        projectUuid: ctxForm.projectUuid || selectedProjectId, // optional override
        s3Prefix: ctxForm.s3Prefix, // optional
      });

      // 4) Get AWS credentials in a simple way (v2 SDK)
      if (!AWS?.config?.credentials) {
        throw new Error("AWS.config.credentials not set");
      }
      await AWS.config.credentials.getPromise(); // ensure resolved
      const awsCreds = AWS.config.credentials;

      // 5) Persist only what changed (patch). EntityManager.update merges + stamps.
      const saved = await mgr.update({
        vars,
        patch: { image: imageDataUrl },
        aws: awsCreds,
      });

      // 6) (Optional) reflect locally so UI updates immediately
      // setRoomOptions?.((prev) =>
      //   prev.map((r) => (r.id === roomIdStr ? { ...r, image: imageDataUrl } : r))
      // );

      return saved;
    } catch (err) {
      console.error("[Room Save][error]", err);
      setSaveError(err?.message || String(err));
      throw err;
    } finally {
      setSavingImage(false);
    }
  }

  // ==========================================================================

  // ---- Upload flow (reliable: uses mounted hidden input) ----
  function openFilePicker(globalId) {
    uploadglobalIdRef.current = globalId;
    fileInputRef.current?.click();
  }

  function onFilePicked(e) {
    const globalId = uploadglobalIdRef.current;
    const file = e.target.files?.[0];
    if (!file || !globalId) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      updateRoomImage(globalId, dataUrl);
      // Persist to Room entity
      saveRoomImage(globalId, dataUrl);
    };
    reader.onerror = () => console.error("File read failed");
    reader.readAsDataURL(file);
    // allow picking the same file again later
    e.target.value = "";
  }

  // ---- Camera modal (React-based) ----
  function openCamera(globalId) {
    setCameraDropdown({ open: null });
    setCameraModal({ open: true, globalId });
  }
  function closeCamera() {
    setCameraModal({ open: false, globalId: null });
  }

  // Helper to render a sensible project label
  const projectLabel = (p) =>
    p?.projectName || p?.name || p?.address || p?.townStateZip || p?.id;

  return (
    <aside className="project-bar">
      {/* Hidden file input used for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      {/* Project selector */}
      <div className="projects-header">
        <select
          id="project-select"
          value={selectedProjectId || ""}
          onChange={(e) => onProjectSelect?.(e.target.value)}
          className="project-select"
        >
          <option value="" disabled>
            Select a project...
          </option>
          {projects.filter(Boolean).map((p) => (
            <option key={p.id} value={p.id}>
              {projectLabel(p)}
            </option>
          ))}
        </select>
      </div>

      {/* Room selector + New */}
      <div className="room-selection">
        <div className="room-select-row">
          <select
            id="room-select"
            value={selectedRoom || ""}
            onChange={handleRoomSelectionChange}
            className="room-select"
          >
            <option value="" disabled>
              Select a room...
            </option>
            {roomOptions.map((room) => (
              <option key={room.id} value={room.id}>
                {room.title}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="room-new-btn"
            onClick={() =>
              setNewRoomUI((u) => ({ ...u, open: !u.open, error: null }))
            }
            disabled={!selectedProjectId && !ctxForm.projectUuid}
            title={
              !selectedProjectId && !ctxForm.projectUuid
                ? "Select a project first"
                : "Create a new room"
            }
          >
            ＋ New
          </button>
        </div>

        {newRoomUI.open && (
          <div className="room-new-inline">
            <input
              type="text"
              className="room-new-input"
              placeholder="Room name (e.g., Bedroom, Home Office)"
              value={newRoomUI.title}
              onChange={(e) =>
                setNewRoomUI((u) => ({ ...u, title: e.target.value }))
              }
            />
            <button
              type="button"
              className="room-create-btn"
              onClick={createNewRoom}
              disabled={newRoomUI.saving}
            >
              {newRoomUI.saving ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              className="room-cancel-btn"
              onClick={() =>
                setNewRoomUI({
                  open: false,
                  title: "",
                  saving: false,
                  error: null,
                })
              }
              disabled={newRoomUI.saving}
            >
              Cancel
            </button>
            {newRoomUI.error && (
              <div className="room-new-error">{newRoomUI.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Room card */}
      {roomDetails && (
        <div className="room-project-layout">
          <div className="room-project-top-row">
            <div className="room-card-thumbnail" aria-label="RoomCard">
              <div className="room-card-box">
                <div className="room-card-image-container">
                  {roomDetails.image ? (
                    <img
                      src={roomDetails.image}
                      alt={roomDetails.title}
                      className="room-card-image"
                    />
                  ) : (
                    <div className="room-card-placeholder">
                      📷 No image uploaded
                    </div>
                  )}

                  {/* Saving indicator */}
                  {savingImage && (
                    <div className="absolute top-2 left-2 text-xs bg-white/90 border rounded px-2 py-1">
                      Saving…
                    </div>
                  )}
                  {saveError && (
                    <div className="absolute top-2 left-2 text-xs bg-red-50 border border-red-300 rounded px-2 py-1 text-red-700">
                      Save failed
                    </div>
                  )}

                  <button
                    type="button"
                    className="camera-button absolute top-2 right-2 bg-white/90 border border-gray-300 rounded px-2 py-1 text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCameraDropdown({ open: roomDetails.id });
                    }}
                  >
                    📷
                  </button>

                  {cameraDropdown.open === roomDetails.id && (
                    <div className="camera-dropdown absolute top-10 right-2 bg-white border border-gray-200 rounded shadow-md z-10">
                      <button
                        className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                        onClick={() => openCamera(roomDetails.id)}
                      >
                        📸 Use Camera
                      </button>
                      <button
                        className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                        onClick={() => {
                          setCameraDropdown({ open: null });
                          openFilePicker(roomDetails.id);
                        }}
                      >
                        🖼️ Upload Image
                      </button>
                    </div>
                  )}
                </div>
                <div className="room-card-content">
                  <h3 className="room-title">{roomDetails.title}</h3>
                  <p className="room-description">{roomDetails.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan */}
      <PlanModule
        selectedRoom={selectedRoom}
        onStartChat={() => {
          // swap to your chat panel/tab here, e.g. setActiveTab("chat")
        }}
        maxTasks={4}
      />

      {/* Camera Modal */}
      {cameraModal.open && (
        <CameraModal
          globalId={cameraModal.globalId}
          facingMode={facingMode}
          setFacingMode={setFacingMode}
          onClose={closeCamera}
          onSnap={(dataUrl) => {
            updateRoomImage(cameraModal.globalId, dataUrl);
            // Persist to Room entity
            saveRoomImage(cameraModal.globalId, dataUrl);
          }}
        />
      )}
    </aside>
  );
}

/** Camera modal implemented with React (no direct DOM mutation). */
function CameraModal({ globalId, facingMode, setFacingMode, onClose, onSnap }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        stopStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.playsInline = true;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error("Camera error:", err);
      }
    }

    start();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [facingMode]);

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }

  function takePhoto() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    onSnap(dataUrl);
  }

  function flipCamera() {
    setFacingMode((f) => (f === "user" ? "environment" : "user"));
  }

  return (
    <div className="camera-modal fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-lg shadow-xl p-4 w-full max-w-md">
        <div className="aspect-video bg-black rounded overflow-hidden mb-3">
          <video ref={videoRef} className="w-full h-full object-contain" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            className="px-3 py-2 border rounded"
            onClick={flipCamera}
            type="button"
          >
            🔄 Flip
          </button>
          <button
            className="px-3 py-2 border rounded"
            onClick={takePhoto}
            type="button"
          >
            📸 Snap
          </button>
          <button
            className="px-3 py-2 border rounded"
            onClick={onClose}
            type="button"
          >
            ❌ Cancel
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Room: <code>{globalId}</code> · Camera: <code>{facingMode}</code>
        </div>
      </div>
    </div>
  );
}
